import { useState } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import {
  Menu,
  Plane,
  FileText,
  LogOut,
  BarChart3,
  Calculator,
  Scale,
  LayoutDashboard,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { RoleSwitcher } from '@/components/ui/role-switcher';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { AmadeusStatusIndicator } from './AmadeusStatusIndicator';

// Main navigation items
const mainNavItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/lawyer/dashboard',
  },
  {
    id: 'cases',
    label: 'Fälle',
    icon: FileText,
    path: '/lawyer/cases',
  },
];

// Tools section
const toolsNavItems = [
  {
    id: 'bookings',
    label: 'Buchungen',
    icon: Plane,
    path: '/lawyer/bookings',
  },
  {
    id: 'pricing',
    label: 'Tarifpreise',
    icon: Calculator,
    path: '/lawyer/pricing',
  },
  {
    id: 'flight-status',
    label: 'Flugstatus',
    icon: BarChart3,
    path: '/lawyer/flight-status',
  },
];

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { organizationName, logoUrl } = useTheme();

  const handleNavigation = (path: string) => {
    navigate({ to: path });
    setOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate({ to: '/login' });
  };

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile header bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-sidebar h-14 border-b border-sidebar-border">
        <div className="flex items-center justify-between px-4 h-full">
          <button
            onClick={() => setOpen(true)}
            className="p-2 -ml-2 rounded-lg hover:bg-sidebar-border transition-colors"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6 text-sidebar-text" />
          </button>

          {/* Center logo */}
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt={organizationName} className="h-8 w-auto" />
            ) : (
              <>
                <Scale className="h-5 w-5 text-brand-from" />
                <span className="text-base font-semibold bg-gradient-to-r from-brand-from to-brand-to bg-clip-text text-transparent">
                  {organizationName}
                </span>
              </>
            )}
          </div>

          {/* Spacer for centering */}
          <div className="w-10" />
        </div>
      </div>

      {/* Slide-out drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          className="w-72 p-0 bg-sidebar border-sidebar-border"
        >
          {/* Header */}
          <div className="flex items-center justify-between h-14 px-4 border-b border-sidebar-border">
            <div className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt={organizationName} className="h-8 w-auto" />
              ) : (
                <>
                  <Scale className="h-5 w-5 text-brand-from" />
                  <span className="text-base font-semibold bg-gradient-to-r from-brand-from to-brand-to bg-clip-text text-transparent">
                    {organizationName}
                  </span>
                </>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-2 -mr-2 rounded-lg hover:bg-sidebar-border transition-colors"
              aria-label="Close menu"
            >
              <X className="h-5 w-5 text-sidebar-text" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-2 overflow-y-auto">
            {/* Main Section */}
            <div className="space-y-1">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 min-h-[48px]',
                      active
                        ? 'bg-sidebar-active text-action-text shadow-lg shadow-sidebar-active/25'
                        : 'text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-border'
                    )}
                  >
                    <Icon className={cn('h-5 w-5 shrink-0', active && 'text-action-text')} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Tools Section */}
            <div className="mt-6 pt-4 border-t border-sidebar-border">
              <p className="text-xs text-sidebar-text/70 px-3 mb-2 uppercase tracking-wider">
                Werkzeuge
              </p>
              <div className="space-y-1">
                {toolsNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 min-h-[48px]',
                        active
                          ? 'bg-sidebar-active text-action-text shadow-lg shadow-sidebar-active/25'
                          : 'text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-border'
                      )}
                    >
                      <Icon className={cn('h-5 w-5 shrink-0', active && 'text-action-text')} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Role Switcher */}
          {user?.is_lawyer && user?.is_client && (
            <div className="border-t border-sidebar-border p-3">
              <p className="text-xs text-sidebar-text/70 mb-2 px-1">Ansicht wechseln</p>
              <RoleSwitcher variant="vertical" size="sm" showLabels={true} />
            </div>
          )}

          {/* Amadeus Status */}
          <div className="border-t border-sidebar-border">
            <AmadeusStatusIndicator isCollapsed={false} />
          </div>

          {/* User Section */}
          <div className="border-t border-sidebar-border p-4">
            {user && (
              <div className="mb-3 px-2">
                <p className="text-sm font-medium text-sidebar-text-hover truncate">
                  {user.email}
                </p>
                <p className="text-xs text-sidebar-text/70">Anwalt</p>
              </div>
            )}

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sidebar-text hover:text-red-500 hover:bg-red-50 transition-colors min-h-[48px]"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Abmelden</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
